import { useCallback, useEffect, useRef, useState } from 'react';
import type { MapOptions } from 'mapbox-gl';
import amplitudeLog from '@client/amplitude';
import {
  AMP_DESIGN_PLACE_BUILDING,
  AMP_DESIGN_RESULTS_UPDATE,
} from '@shared/constants';
import { DesignMap } from '../map';
import { Coordinate } from '@shared/models/geography.model';
import { Shape, Storey } from '@shared/models/design/storey.model';
import {
  getBuildingById,
  getBuildingStoreys,
  setBuildingStoreysWithProgramSeed,
} from '@shared/helpers/design/storey.helpers';
import {
  getBuildingDesign,
  useUpdateBuildingDesign,
  useUpdateBuildingLocation,
} from './design.hooks';
import { useDeselectBuilding, useSelectBuilding } from './storeys.hooks';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { BUILDING_OVERLAP_WARNING } from '../helpers/building-overlap.constants';
import { Project } from '@shared/models/project.model';
import { getBuildingDesignFromProjectOrBuilding } from '@shared/helpers/design/design.helpers';
import {
  DesignSnapshotSupersededError,
  isDesignSnapshotSupersededError,
} from '../helpers/design-snapshot-persist.helpers';
import {
  getDesignMapCamera,
  getSelectedStoreyId,
  getSelectedBuildingId,
  useRequestPropertiesPanel,
  useSelectBuildingOutline,
  useSetHoveredBuildingId,
  useSetSelectedStoreyId,
} from '../store/design-store.hooks';
import { applyDesignMapLocationAndStoreys } from '../helpers/location-origin-rebase.helpers';
import { designCommitEvents } from '@shared/helpers/project-events/design-commit-events.helpers';
import { getBuildingOpenStoreyId } from '../helpers/storey-selection.helpers';
import { planBuildingDuplicate } from '../helpers/building-duplicate.helpers';
import { moveBuildingBlueprintUnderlaysTo } from '@shared/helpers/design/blueprint-underlay.helpers';
import {
  clearProjectOpeningCamera,
  getProjectOpeningCamera,
  resolveDesignMapAttachHandoff,
} from '../helpers/map-camera-handoff.helpers';

type LatestRef<T> = {
  readonly current: T;
};
const useLatestRef = <T>(value: T): LatestRef<T> => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

type UseDesignMapInstanceArgs = {
  accessToken?: string;
  location: Coordinate | undefined;
  /**
   * Mapbox options forwarded to {@link DesignMap.attach}. Merged on top of the
   * project defaults so callers can override style, interactivity, etc. The
   * reference is captured on first mount; later changes do not re-attach.
   */
  attachOptions?: Omit<MapOptions, 'container' | 'center'>;
  /**
   * Restore the last Design camera and panel-aware projection at attach time
   * so Design ↔ Build keeps the view. The camera is only reused when the stored
   * session belongs to this project, so switching projects starts fresh.
   */
  sessionCameraProjectId?: number;
};

type UseDesignMapInstanceResult = {
  map: DesignMap | undefined;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  openingFromProjectList: boolean;
};

export const useDesignMapInstance = ({
  accessToken,
  location,
  attachOptions,
  sessionCameraProjectId,
}: UseDesignMapInstanceArgs): UseDesignMapInstanceResult => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initialLocationRef = useRef<Coordinate | undefined>(location);
  // Capture attach options once so prop identity changes don't tear down the map.
  const attachOptionsRef = useRef(attachOptions);
  const sessionCameraProjectIdRef = useRef(sessionCameraProjectId);
  const openingCameraRef = useRef(
    sessionCameraProjectId === undefined
      ? undefined
      : getProjectOpeningCamera(sessionCameraProjectId),
  );
  const [map, setMap] = useState<DesignMap | undefined>(undefined);
  const [initialLocationReady, setInitialLocationReady] = useState(
    Boolean(initialLocationRef.current),
  );

  useEffect(() => {
    if (!initialLocationRef.current && location) {
      initialLocationRef.current = location;
      setInitialLocationReady(true);
    }
  }, [location]);

  useEffect(() => {
    if (!accessToken || !initialLocationReady) return;
    const container = containerRef.current;
    const initialLocation = initialLocationRef.current;
    if (!container || !initialLocation) return;

    const designMap = new DesignMap(accessToken, initialLocation);
    let cancelled = false;

    // Snapshot session camera at attach so remount restores zoom/angle/center.
    const cameraProjectId = sessionCameraProjectIdRef.current;
    const sessionCamera =
      cameraProjectId === undefined
        ? undefined
        : getDesignMapCamera(cameraProjectId);
    const handoff = resolveDesignMapAttachHandoff(
      sessionCamera,
      openingCameraRef.current,
    );

    (async () => {
      try {
        await designMap.attach(container, {
          ...attachOptionsRef.current,
          ...handoff.attachOptions,
        });
        if (handoff.restoreSessionFacade)
          designMap.restoreFacadeCameraState(handoff.restoreSessionFacade);
        if (!cancelled) {
          if (openingCameraRef.current)
            clearProjectOpeningCamera(openingCameraRef.current);
          setMap(designMap);
        }
      } catch (_error) {
        if (!cancelled) setMap(undefined);
      }
    })();

    return () => {
      cancelled = true;
      designMap.destroy();
      setMap(undefined);
    };
  }, [accessToken, initialLocationReady]);

  return {
    map,
    containerRef,
    openingFromProjectList: Boolean(openingCameraRef.current),
  };
};

type UseDesignMapStateSyncArgs = {
  map: DesignMap | undefined;
  location: Coordinate | undefined;
  storeys: Storey[];
  selectedStoreyId: Storey['id'] | undefined;
  selectedBuildingId?: string;
};

export const useDesignMapStateSync = ({
  map,
  location,
  storeys,
  selectedStoreyId,
  selectedBuildingId,
}: UseDesignMapStateSyncArgs): void => {
  useEffect(() => {
    if (!map) return;
    applyDesignMapLocationAndStoreys(map, {
      location,
      storeys,
      selectedBuildingId,
    });
  }, [map, location, storeys, selectedBuildingId]);

  useEffect(() => {
    if (!map) return;
    map.selectStorey(selectedStoreyId);
  }, [map, selectedStoreyId]);
};

type UseDesignMapInteractionsArgs = {
  map: DesignMap | undefined;
  storeys: Storey[];
  shapes: Shape[];
  selectedStoreyId: Storey['id'] | undefined;
  readonly?: boolean;
};

export const useDesignMapInteractions = ({
  map,
  storeys,
  shapes: _shapes,
  selectedStoreyId: _selectedStoreyId,
  readonly,
}: UseDesignMapInteractionsArgs): void => {
  const storeysRef = useLatestRef(storeys);
  const commitSequenceRef = useRef(0);
  const updateBuildingDesign = useUpdateBuildingDesign();
  const updateBuildingLocation = useUpdateBuildingLocation();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const selectBuilding = useSelectBuilding();
  const deselectBuilding = useDeselectBuilding();
  const setHoveredBuildingId = useSetHoveredBuildingId();
  const selectBuildingOutline = useSelectBuildingOutline();
  const requestPropertiesPanel = useRequestPropertiesPanel();
  const { enqueueSnackbar } = useSnackbar();

  const resetOnFailure = useCallback(
    async (
      promise: Promise<Project>,
      buildingId: string | undefined,
      sequence: number,
    ): Promise<Project | undefined> => {
      try {
        const response = await promise;
        const newDesign = getBuildingDesignFromProjectOrBuilding(response);
        const newStoreys = getBuildingStoreys(newDesign, buildingId);

        // Reset storeys on error
        if (
          sequence === commitSequenceRef.current &&
          newStoreys === storeysRef.current
        ) {
          map?.setStoreys(storeysRef.current, { buildingId });
        }
        return response;
      } catch (error: unknown) {
        if (isDesignSnapshotSupersededError(error)) {
          // Cancellation: paint the winning design and return undefined to signal
          if (sequence === commitSequenceRef.current) {
            map?.setStoreys(
              getBuildingStoreys(getBuildingDesign(), buildingId),
              { buildingId },
            );
          }
          return undefined;
        }
        throw error;
      }
    },
    [map, storeysRef],
  );

  useEffect(() => {
    if (!map) return;
    map.setReadonly(Boolean(readonly));
  }, [map, readonly]);

  useEffect(() => {
    if (!map || readonly) return;

    map.enableBuildingDuplication(async (buildingId, delta) => {
      const source = getBuildingById(getBuildingDesign(), buildingId);
      if (!source) return;
      const plan = planBuildingDuplicate(source, delta);
      if (!plan) return;
      const rollbackUnsavedCopy = (savedBuildings: { id: string }[]): void => {
        if (
          savedBuildings.some((building) => building.id === plan.building.id) ||
          getSelectedBuildingId() !== plan.building.id
        ) {
          return;
        }
        selectBuilding(source.id);
        selectBuildingOutline(source.id);
      };
      try {
        const response = await updateBuildingDesign(
          (design) => ({
            ...design,
            buildings: [...design.buildings, plan.building],
          }),
          {
            events: plan.events,
            onOptimisticLocalUpdate: () => {
              selectBuilding(plan.building.id);
              selectBuildingOutline(plan.building.id);
              requestPropertiesPanel();
            },
          },
        );
        rollbackUnsavedCopy(
          getBuildingDesignFromProjectOrBuilding(response).buildings,
        );
      } catch (error: unknown) {
        if (!isDesignSnapshotSupersededError(error)) throw error;
        rollbackUnsavedCopy(getBuildingDesign().buildings);
      }
    });

    map.enableCommit({
      onCommit: async (storeysToCommit, buildingId, reason) => {
        const sequence = ++commitSequenceRef.current;
        const response = await resetOnFailure(
          updateBuildingDesign(
            (design) => {
              // An unset id is the first building, which the map is editing.
              const resolvedId = buildingId ?? design.buildings[0]?.id;
              // The building's PDF pages follow it when it moves or turns.
              const withPages =
                reason === 'building-move' && resolvedId
                  ? moveBuildingBlueprintUnderlaysTo(
                      design,
                      resolvedId,
                      storeysToCommit,
                    )
                  : design;
              return setBuildingStoreysWithProgramSeed(
                withPages,
                buildingId,
                storeysToCommit,
              );
            },
            {
              events: (design) => {
                // An unset id is the first building, which the map is editing.
                const resolvedId = buildingId ?? design.buildings[0]?.id;
                if (!resolvedId) return [];
                return designCommitEvents({
                  buildingId: resolvedId,
                  previous: getBuildingStoreys(design, resolvedId),
                  next: storeysToCommit,
                  reason,
                });
              },
            },
          ),
          buildingId,
          sequence,
        );
        // undefined signals that the save was cancelled by a superseding change.
        // Throw so persistStoreys can catch and return false.
        if (!response) {
          throw new DesignSnapshotSupersededError();
        }
        amplitudeLog(AMP_DESIGN_RESULTS_UPDATE);
      },
    });

    map.enableBuildingSelection({
      onSelect: (buildingId) => {
        // Preserve abandoned-building cleanup before selecting the whole building.
        selectBuilding(buildingId);
        selectBuildingOutline(buildingId);
        requestPropertiesPanel();
      },
      onOpen: (buildingId, storeyId) => {
        // Override preceding single clicks with the lowest floor hit at the pointer.
        selectBuilding(buildingId);
        setSelectedStoreyId(
          storeyId ??
            getBuildingOpenStoreyId(
              getBuildingStoreys(getBuildingDesign(), buildingId),
            ),
        );
        requestPropertiesPanel();
      },
      onDeselect: () => {
        deselectBuilding();
      },
      onHover: (buildingId) => {
        setHoveredBuildingId(buildingId);
      },
    });

    map.enableSiblingBuildingOverlapWarning({
      onOverlap: () => {
        enqueueSnackbar(BUILDING_OVERLAP_WARNING, { variant: 'warning' });
      },
    });

    map.enableBuildingTranslation({
      onCommit: async (location, reason) => {
        const persisted = await updateBuildingLocation(location, { reason });
        // A newer location request already owns the pin. This drag did not land.
        if (!persisted) return;
        amplitudeLog(AMP_DESIGN_PLACE_BUILDING);
        return persisted;
      },
    });
    map.enableStoreyNavigation({
      onNavigate: (direction) => {
        const storeys = storeysRef.current;
        if (!storeys.length) return;
        if (direction === 'tab') {
          const currentId = getSelectedStoreyId();
          const currentIndex = storeys.findIndex((s) => s.id === currentId);
          const nextIndex = (currentIndex + 1) % storeys.length;
          setSelectedStoreyId(storeys[nextIndex]?.id);
        } else {
          setSelectedStoreyId(storeys[direction - 1]?.id);
        }
      },
    });
    map.enableStoreySelection({
      onSelect: (storeyId) => {
        setSelectedStoreyId(storeyId);
        requestPropertiesPanel();
      },
    });
  }, [
    map,
    readonly,
    setSelectedStoreyId,
    storeysRef,
    updateBuildingDesign,
    updateBuildingLocation,
    resetOnFailure,
    selectBuilding,
    selectBuildingOutline,
    requestPropertiesPanel,
    deselectBuilding,
    setHoveredBuildingId,
    enqueueSnackbar,
  ]);
};
